import { useState } from "react";
import { useQuery } from "@apollo/client";

import Header from "../../../../components/ui/ListHeader";
import { FETCH_CHANGE_LOGS } from "../../../../graphql/query/ChangeLog";
import {
  ACTIVE_REPORT_TYPES,
  activeReportExportInput,
  activeReportExportLabel,
  activeReportExportScope,
  formatActiveReportDuration,
} from "./utils";

const PAGE_SIZE = 25;

const StatusBadge = ({ status }) => (
  <span
    className={`inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold ${
      status === "completed"
        ? "bg-green-100 text-green-800"
        : status === "failed"
          ? "bg-red-100 text-red-800"
          : "bg-gray-100 text-gray-700"
    }`}
  >
    {status || "Unknown"}
  </span>
);

const formatDate = value => {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date.toLocaleString() : "—";
};

const ActiveReportExports = () => {
  const [filters, setFilters] = useState({
    correlationId: "",
    from: "",
    offset: 0,
    reportTemplateId: "",
    reportType: "",
    to: "",
    userId: "",
  });
  const input = activeReportExportInput({
    ...filters,
    limit: PAGE_SIZE,
  });
  const { data, loading, error } = useQuery(FETCH_CHANGE_LOGS({ user: {} }), {
    variables: { input },
    fetchPolicy: "cache-and-network",
  });

  const changeLogs = data?.changeLogs?.edges || [];
  const count = data?.changeLogs?.count || 0;
  const page = Math.floor(filters.offset / PAGE_SIZE);
  const pageCount = Math.max(Math.ceil(count / PAGE_SIZE), 1);

  const updateFilter = event => {
    const { name, value } = event.target;
    setFilters(current => ({ ...current, [name]: value, offset: 0 }));
  };

  return (
    <>
      <Header
        leftText="Active report exports"
        rightComponent={() => (
          <span className="text-sm text-gray-500">
            {count} matching export{count === 1 ? "" : "s"}
          </span>
        )}
      />
      <main className="px-4 pb-8 sm:px-6 lg:px-8">
        <div className="my-6 grid grid-cols-1 gap-4 rounded-md border border-gray-200 bg-gray-50 p-4 sm:grid-cols-2 xl:grid-cols-6">
          <label className="text-xs font-medium uppercase text-gray-500">
            Type
            <select
              className="mt-1 block w-full rounded border-gray-300 bg-white text-sm"
              name="reportType"
              value={filters.reportType}
              onChange={updateFilter}
            >
              <option value="">All</option>
              {ACTIVE_REPORT_TYPES.map(type => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs font-medium uppercase text-gray-500">
            Template ID
            <input
              className="mt-1 block w-full rounded border-gray-300 bg-white text-sm"
              min="1"
              name="reportTemplateId"
              type="number"
              value={filters.reportTemplateId}
              onChange={updateFilter}
            />
          </label>
          <label className="text-xs font-medium uppercase text-gray-500">
            Correlation ID
            <input
              className="mt-1 block w-full rounded border-gray-300 bg-white text-sm"
              name="correlationId"
              value={filters.correlationId}
              onChange={updateFilter}
              placeholder="UUID or prefix"
            />
          </label>
          <label className="text-xs font-medium uppercase text-gray-500">
            User ID
            <input
              className="mt-1 block w-full rounded border-gray-300 bg-white text-sm"
              min="1"
              name="userId"
              type="number"
              value={filters.userId}
              onChange={updateFilter}
            />
          </label>
          <label className="text-xs font-medium uppercase text-gray-500">
            From
            <input
              className="mt-1 block w-full rounded border-gray-300 bg-white text-sm"
              name="from"
              type="date"
              value={filters.from}
              onChange={updateFilter}
            />
          </label>
          <label className="text-xs font-medium uppercase text-gray-500">
            To
            <input
              className="mt-1 block w-full rounded border-gray-300 bg-white text-sm"
              name="to"
              type="date"
              value={filters.to}
              onChange={updateFilter}
            />
          </label>
        </div>

        {error ? (
          <div className="mb-4 text-sm text-red-600">
            Failed to load ActiveReport exports: {error.message}
          </div>
        ) : null}
        {loading && changeLogs.length === 0 ? (
          <div className="text-sm text-gray-500">Loading…</div>
        ) : null}
        {!loading && changeLogs.length === 0 && !error ? (
          <div className="text-sm text-gray-500">
            No ActiveReport exports match these filters.
          </div>
        ) : null}

        {changeLogs.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] border-collapse text-left">
              <thead>
                <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
                  <th className="py-2 pr-4">Export</th>
                  <th className="px-4 py-2">Requested by</th>
                  <th className="px-4 py-2">Scope</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2">Duration</th>
                  <th className="px-4 py-2">Date</th>
                  <th className="py-2 pl-4">Details</th>
                </tr>
              </thead>
              <tbody>
                {changeLogs.map(changeLog => {
                  const { meta = {} } = changeLog;
                  const correlationId = meta.correlationId;
                  return (
                    <tr
                      key={changeLog.id}
                      className="border-b border-gray-100 text-sm"
                    >
                      <td className="py-3 pr-4 align-top">
                        <div className="font-medium text-gray-900">
                          #{changeLog.id} · {activeReportExportLabel(changeLog)}
                        </div>
                        {correlationId ? (
                          <button
                            type="button"
                            className="mt-1 font-mono text-xs text-blue-600 hover:text-blue-800"
                            title="Copy correlation ID"
                            onClick={() =>
                              navigator.clipboard?.writeText(correlationId)
                            }
                          >
                            {correlationId}
                          </button>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 align-top">
                        <div>
                          {changeLog.user?.meta?.name ||
                            changeLog.user?.email ||
                            `User ${changeLog.userId}`}
                        </div>
                        {changeLog.user?.meta?.name ? (
                          <div className="text-xs text-gray-500">
                            {changeLog.user.email}
                          </div>
                        ) : null}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 align-top text-gray-500">
                        {activeReportExportScope(changeLog)}
                      </td>
                      <td className="px-4 py-3 align-top">
                        <StatusBadge status={meta.result?.status} />
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 align-top text-gray-500">
                        {formatActiveReportDuration(meta.result?.durationMs)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 align-top text-gray-500">
                        {formatDate(changeLog.createdAt)}
                      </td>
                      <td className="py-3 pl-4 align-top">
                        <details>
                          <summary className="cursor-pointer whitespace-nowrap font-medium text-blue-600 hover:text-blue-800">
                            View input
                          </summary>
                          <pre className="mt-2 max-h-96 max-w-2xl overflow-auto whitespace-pre-wrap break-all rounded bg-gray-50 p-3 text-left text-xs text-gray-700">
                            {JSON.stringify(
                              {
                                input: meta.input,
                                resolvedIds: meta.resolvedIds,
                                result: meta.result,
                              },
                              null,
                              2,
                            )}
                          </pre>
                        </details>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}

        {changeLogs.length > 0 ? (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm">
            <span className="text-gray-500">
              {filters.offset + 1}–
              {Math.min(filters.offset + changeLogs.length, count)} of {count}
            </span>
            <div className="flex items-center gap-4">
              <button
                type="button"
                className="font-medium text-blue-600 disabled:text-gray-300"
                disabled={page === 0}
                onClick={() =>
                  setFilters(current => ({
                    ...current,
                    offset: Math.max(current.offset - PAGE_SIZE, 0),
                  }))
                }
              >
                Previous
              </button>
              <span className="text-gray-500">
                Page {page + 1} of {pageCount}
              </span>
              <button
                type="button"
                className="font-medium text-blue-600 disabled:text-gray-300"
                disabled={page >= pageCount - 1}
                onClick={() =>
                  setFilters(current => ({
                    ...current,
                    offset: current.offset + PAGE_SIZE,
                  }))
                }
              >
                Next
              </button>
            </div>
          </div>
        ) : null}
      </main>
    </>
  );
};

export default ActiveReportExports;
